import { useState, useEffect, useRef } from 'react';
import { Question, QuestionResult, GameStatistics, GameDisplayRoomState } from '../types/game';
import { calculateTimeRemaining, getQuestionId, isNewQuestion } from '../utils/gameUtils';
import { HINT_DELAY, FADE_OUT_DURATION } from '../constants/gameConstants';
import { useGameEndScreenData } from './useGameEndScreenData';

export interface HostPlayingState {
  selectedAnswer: string | null;
  selectedPairs?: Record<string, string>; // For pair matching games
  answerSubmitted: boolean;
  answerCorrect: boolean | null;
  waitingForOthers: boolean;
  isSubmitting: boolean;
}

export type LeaderboardEntry = { playerId: string; playerName: string; score: number };

interface UseGameStateReturn {
  currentQuestion: Question | null;
  timeRemaining: number;
  questionResult: QuestionResult | null;
  shouldUpdateStandings: boolean;
  /** When set, use this for standings (from question-result) so green points show even if room-state is delayed (prod race). */
  leaderboardFromResult: LeaderboardEntry[] | null;
  gameEnded: boolean;
  finalLeaderboard: Array<{ playerId: string; playerName: string; score: number }>;
  gameStatistics: GameStatistics | null;
  waitingForNewGame: boolean;
  showHint: boolean;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<Question | null>>;
  setTimeRemaining: React.Dispatch<React.SetStateAction<number>>;
  setQuestionResult: React.Dispatch<React.SetStateAction<QuestionResult | null>>;
  setShouldUpdateStandings: React.Dispatch<React.SetStateAction<boolean>>;
  setLeaderboardFromResult: React.Dispatch<React.SetStateAction<LeaderboardEntry[] | null>>;
  setGameEnded: React.Dispatch<React.SetStateAction<boolean>>;
  setFinalLeaderboard: React.Dispatch<React.SetStateAction<Array<{ playerId: string; playerName: string; score: number }>>>;
  setGameStatistics: React.Dispatch<React.SetStateAction<GameStatistics | null>>;
  setWaitingForNewGame: React.Dispatch<React.SetStateAction<boolean>>;
  setShowHint: React.Dispatch<React.SetStateAction<boolean>>;
  resetHostPlayingState: () => void;
  hostPlayingState: HostPlayingState;
  setHostPlayingState: React.Dispatch<React.SetStateAction<HostPlayingState>>;
  previousQuestionIdRef: React.MutableRefObject<string | undefined>;
  lastLoggedQuestionClearedRef: React.MutableRefObject<string | undefined>;
}

/**
 * Hook to manage main game state
 */
export const useGameState = (
  roomState: GameDisplayRoomState,
  onQuestionChange?: (question: Question | null) => void
): UseGameStateReturn => {
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [shouldUpdateStandings, setShouldUpdateStandings] = useState(false);
  const [leaderboardFromResult, setLeaderboardFromResult] = useState<LeaderboardEntry[] | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const hintShownRef = useRef(false);
  
  const [hostPlayingState, setHostPlayingState] = useState<HostPlayingState>({
    selectedAnswer: null,
    selectedPairs: {},
    answerSubmitted: false,
    answerCorrect: null,
    waitingForOthers: false,
    isSubmitting: false,
  });

  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const lastLoggedQuestionClearedRef = useRef<string | undefined>(undefined);

  const gameEndData = useGameEndScreenData(roomState);

  const resetHostPlayingState = () => {
    setHostPlayingState({
      selectedAnswer: null,
      selectedPairs: {},
      answerSubmitted: false,
      answerCorrect: null,
      waitingForOthers: false,
      isSubmitting: false,
    });
  };

  // Update local state when roomState.currentQuestion changes - but only if it's a different question
  useEffect(() => {
    // When server says game is finished, always show game end (reconnection or missed game-ended event)
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setTimeRemaining(0);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics((prev) => gameEndData.gameStatistics ?? prev);
      return;
    }

    if (roomState.currentQuestion) {
      const newQuestionId = getQuestionId(roomState.currentQuestion);
      
      // Calculate remaining time
      const pauseOptions = {
        isPaused: roomState.isPaused,
        pausedRemainingMs: roomState.pausedRemainingMs,
      };
      const calculatedTimeRemaining = calculateTimeRemaining(
        roomState.currentQuestion,
        roomState.questionStartTime,
        pauseOptions
      );
      
      if (calculatedTimeRemaining === roomState.currentQuestion.timeRemaining) {
        console.log(`GameDisplay: Using backend-calculated remaining time from room-state: ${calculatedTimeRemaining}ms`);
      } else if (roomState.questionStartTime) {
        const elapsed = Date.now() - roomState.questionStartTime;
        console.log(`GameDisplay: Calculated remaining time from questionStartTime: ${calculatedTimeRemaining}ms (elapsed: ${elapsed}ms, limit: ${roomState.currentQuestion.timeLimit}ms)`);
      } else {
        console.log(`GameDisplay: Using full timeLimit from room-state: ${calculatedTimeRemaining}ms`);
      }
      
      // CRITICAL: Only update if it's a NEW question (different ID) OR if we don't have a current question yet (reconnection)
      const isNew = isNewQuestion(newQuestionId, previousQuestionIdRef.current);
      const isReconnection = !currentQuestion;
      
      if (isNew || isReconnection) {
        if (isNew) {
          console.log(`GameDisplay: Updating question from room-state. New questionId: ${newQuestionId}, Previous: ${previousQuestionIdRef.current}, IsNewQuestion: ${isNew}`);
        } else {
          console.log(`GameDisplay: Refreshing question state (reconnection). ID: ${newQuestionId}, TimeRemaining: ${calculatedTimeRemaining}ms`);
        }
        previousQuestionIdRef.current = newQuestionId;
        // Preserve Question X of Y when room-state omits numbers (common after bonus / DO wake).
        const enrichedQuestion = {
          ...roomState.currentQuestion,
          questionNumber:
            typeof roomState.currentQuestion.questionNumber === 'number'
              ? roomState.currentQuestion.questionNumber
              : currentQuestion?.questionNumber,
          totalQuestions:
            typeof roomState.currentQuestion.totalQuestions === 'number'
              ? roomState.currentQuestion.totalQuestions
              : currentQuestion?.totalQuestions,
        };
        setCurrentQuestion(enrichedQuestion);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionResult(null);
        // Reset hint for new question
        setShowHint(false);
        hintShownRef.current = false;
        // Reset standings update flag. Do not clear leaderboardFromResult here (keep previous
        // question's scores to avoid total column flicker when room-state is stale).
        setShouldUpdateStandings(false);
        // Reset host playing state for new question or when reconnecting (so we don't stay stuck with
        // answerSubmitted true from before lock/disconnect, which would make submit do nothing).
        if (isNew || isReconnection) {
          resetHostPlayingState();
        }
        
        // For collaborative puzzles, show hint after delay if hint is available
        if (roomState.currentQuestion.isCollaborative && roomState.currentQuestion.hint) {
          setTimeout(() => {
            if (newQuestionId === (previousQuestionIdRef.current || newQuestionId)) {
              setShowHint(true);
              hintShownRef.current = true;
            }
          }, HINT_DELAY);
        }

        // Call optional callback
        if (onQuestionChange) {
          onQuestionChange(roomState.currentQuestion);
        }
      } else if (
        newQuestionId === previousQuestionIdRef.current &&
        roomState.gameState === 'playing' &&
        !roomState.isPaused
      ) {
        // Same question, game still playing: sync timer from server (e.g. after tab focus / get-room-state)
        // so player timer continues from same value as host instead of resetting or drifting.
        // Skip while paused — questionStartTime is frozen but wall clock keeps moving.
        setTimeRemaining(calculatedTimeRemaining);
      }
    } else if (roomState.gameState === 'waiting') {
      // Reset state when game goes back to waiting
      setCurrentQuestion(null);
      setQuestionResult(null);
      setTimeRemaining(0); // Stop timer
      setWaitingForNewGame(false);
      setGameEnded(false);
      setLeaderboardFromResult(null); // Clear so next game starts fresh
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
      if (onQuestionChange) {
        onQuestionChange(null);
      }
    } else if (roomState.gameState === 'playing' && !roomState.currentQuestion && currentQuestion) {
      // Question was cleared but game is still playing - this happens between questions
      // Stop the timer immediately - backend will send next question after delay
      const questionId = getQuestionId(currentQuestion);
      if (questionId && lastLoggedQuestionClearedRef.current !== questionId) {
        lastLoggedQuestionClearedRef.current = questionId;
        console.log('GameDisplay: Question cleared but game still playing - stopping timer, waiting for next question');
        setTimeRemaining(0); // Stop timer - backend controls when next question appears
      }
    } else if (roomState.currentQuestion) {
      // Reset the logged ref when a new question arrives
      lastLoggedQuestionClearedRef.current = undefined;
    }
  }, [
    roomState.currentQuestion,
    roomState.questionStartTime,
    roomState.gameState,
    roomState.isPaused,
    roomState.pausedRemainingMs,
    roomState.players,
    currentQuestion,
    onQuestionChange,
    gameEndData,
  ]);

  // Freeze local timer when host pauses the game
  useEffect(() => {
    if (roomState.isPaused && roomState.pausedRemainingMs != null) {
      setTimeRemaining(roomState.pausedRemainingMs);
    }
  }, [roomState.isPaused, roomState.pausedRemainingMs]);

  return {
    currentQuestion,
    timeRemaining,
    questionResult,
    shouldUpdateStandings,
    leaderboardFromResult,
    gameEnded,
    finalLeaderboard,
    gameStatistics,
    waitingForNewGame,
    showHint,
    setCurrentQuestion,
    setTimeRemaining,
    setQuestionResult,
    setShouldUpdateStandings,
    setLeaderboardFromResult,
    setGameEnded,
    setFinalLeaderboard,
    setGameStatistics,
    setWaitingForNewGame,
    setShowHint,
    resetHostPlayingState,
    hostPlayingState,
    setHostPlayingState,
    previousQuestionIdRef,
    lastLoggedQuestionClearedRef,
  };
};
